import { createColumnHelper } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";

import { ActionButton, DateCell } from "@components/tableColumns";
import {
  DeleteActionMenuItem,
  LinkMenuItem,
} from "@components/tableColumns/menuItems/items";
import { path } from "@routes/path.ts";
import type { IDeleteActionParams } from "@/interfaces";
import { Approve } from "@components/tableColumns/cell/approve";
import type { IApproveProps } from "@components/tableColumns/cell/types";
import type { IConferencePaperTable } from "@/template/articlesAndBooks/conferencePaper/table/types";

const columnHelper = createColumnHelper<IConferencePaperTable>();

export const useConferencePaperListColumns = ({
  onDelete,
  ...restProps
}: IDeleteActionParams & IApproveProps) => {
  const { t } = useTranslation("field");

  return [
    columnHelper.accessor("publishYear", {
      header: t("publishedDate"),
      cell: ({ getValue }) => <DateCell date={getValue()} variant={"date"} />,
    }),
    columnHelper.accessor("journalName", {
      header: t("journal"),
    }),
    // TODO: add cell component for text ellipses
    columnHelper.accessor("partners", {
      header: t("partners"),
      cell: ({ getValue }) => getValue()?.join(", "),
    }),
    columnHelper.accessor("paperName", {
      header: t("paperName"),
    }),
    // TODO: add approval status
    columnHelper.accessor("id", {
      id: "approval",
      header: t("approvePaper"),
      cell: ({ getValue, row }) => (
        <Approve
          id={getValue()}
          isApproved={row.original.isApproved}
          {...restProps}
        />
      ),
    }),
    columnHelper.accessor("id", {
      id: "action",
      header: t("action"),
      cell: ({ getValue, row }) => {
        return (
          <ActionButton>
            <LinkMenuItem
              type={"viewPaper"}
              to={path.conferencePapersPath.view({
                conferencePaperId: getValue().toString(),
              })}
            />
            <LinkMenuItem
              type={"editPaper"}
              to={path.conferencePapersPath.edit({
                conferencePaperId: getValue().toString(),
              })}
            />
            <DeleteActionMenuItem
              type={"deletePaper"}
              name={row.original.paperName}
              onClick={async () => {
                await onDelete(getValue());
              }}
            />
          </ActionButton>
        );
      },
    }),
  ];
};
